/*
    Trigger Manager can be used to automatically track click and/or view events
    on any elements on the page which have the necessary `data-oskar` props.
    For example, given the following set up.
    <body>
        <h1
            data-oskar-click="Header Clicked"
            data-oskar-prop-page="/dashboard"
            data-oskar-props="{{ JSON.stringify({items: [1, 2]}) }}"
        >
            Header Text
        <h1>
        <ul>
        <li
            data-oskar-click="Item Clicked"
            data-oskar-view="Item Viewed"
            data-oskar-props="{{ JSON.stringify({num: 1})}}"
        >
            Item #1
        </li>
        <li
            data-oskar-click="Item Clicked"
            data-oskar-view="Item Viewed"
            data-oskar-props="{{ JSON.stringify({num: 2})}}"
        >
            Item #2
        </li>
        <ul>
        <script>
            OskarTS.initOskarTriggers()
        </script>
    </body>
    Clicks on the `h1` tag will trigger oskar to capture "Header Clicked" event with props {page: "/dashboard"}
    Clicks on the `li` tags will trigger oskar to capture "Item Clicked" event with props {num: "1"}
    When entering the viewport `li` tags will trigger oskar to capture "Item Viewed" event with props {num: "1"}
    The trigger manager also accepts a separate `capture` function
    in case you want to wrap Oskar.capture or do something else with the events.
    e.g. OskarTS.initOskarTriggers({ capture: (event, eventProps) => window.dataLayer.push({ event, ...eventProps })});
  */

export interface TriggerOptions {
    capture: (eventName: string, eventProps: { [key: string]: any }) => void;
}

export class TriggerManager {
    private options: TriggerOptions;
    private trackedElements: HTMLElement[] = [];
    private observer: IntersectionObserver;
    private OBSERVER_OPTIONS: IntersectionObserverInit = {
        threshold: 1,
        rootMargin: "0px",
        root: null
    };

    constructor(options: Partial<TriggerOptions> = {}) {
        this.options = { capture: () => {}, ...options };
        this.observer = new IntersectionObserver(this.onIntersection, this.OBSERVER_OPTIONS);
    }

    public initialize() {
        this.addClickListeners(document.querySelectorAll("[data-oskar-click]"));
        this.addViewListeners(document.querySelectorAll("[data-oskar-view]"));
    }

    public refresh() {
        this.removeListeners();
        this.initialize();
    }

    public setOptions(options: Partial<TriggerOptions>) {
        this.options = {
            ...this.options,
            ...options
        };
    }

    private removeListeners() {
        this.removeClickListeners();
        this.observer.disconnect();
    }

    private removeClickListeners() {
        this.trackedElements.forEach(element => {
            element.removeEventListener("click", this.clickListener);
        });
        this.trackedElements = [];
    }

    private addClickListeners(elements: NodeListOf<HTMLElement>) {
        elements.forEach(element => {
            element.addEventListener("click", this.clickListener);
        });
    }

    private clickListener = (e: MouseEvent) => {
        const element = e.currentTarget;
        if (!(element instanceof HTMLElement)) {
            return;
        }
        const eventName = element.dataset.oskarClick;
        this.options.capture(eventName, this.getOskarProps(element));
    };

    private addViewListeners(elements: NodeListOf<HTMLElement>) {
        elements.forEach(element => this.observer.observe(element));
    }

    private onIntersection = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        entries.forEach(entry => {
            const element = entry.target;
            if (!entry.isIntersecting || !(element instanceof HTMLElement)) {
                return;
            }
            observer.unobserve(element);
            this.options.capture(element.dataset.oskarView, this.getOskarProps(element));
        });
    };

    private getOskarProps(element: HTMLElement) {
        try {
            return JSON.parse(element.dataset.oskarProps);
        } catch {
            return {};
        }
    }
}

export default new TriggerManager();
